import { useState } from 'react';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { apiFetch } from '../../api';
import StepProgressBar  from './project-widgets/StepProgressBar';
import Step1BasicInfo   from './project-steps/Step1BasicInfo';
import Step2Location    from './project-steps/Step2Location';
import Step3Financials  from './project-steps/Step3Financials';
import Step4Specs       from './project-steps/Step4Specs';
import Step5Media       from './project-steps/Step5Media';
import Step6Seo         from './project-steps/Step6Seo';

const EMPTY_FORM = {
  name: '', developerId: '', description: '', status: 'OFF_PLAN',
  city: '', district: '', latitude: '', longitude: '', mapEmbedUrl: '',
  startingPrice: '', deliveryDate: '', paymentPlans: [],
  unitTypes: [], amenities: [], totalArea: '',
  coverImage: '', heroVideo: '', gallery: [], masterPlan: '', brochure: '',
  metaTitle: '', metaDesc: '', slug: '',
};

const STEPS = ['Basic Info', 'Location', 'Financials', 'Specs', 'Media', 'SEO'];

function validateStep(step, form) {
  const errs = {};
  if (step === 0) {
    if (!form.name.trim())       errs.name = 'Project name is required';
    if (!form.developerId)       errs.developerId = 'Select or create a developer';
  }
  if (step === 1) {
    if (!form.city.trim())       errs.city = 'City is required';
  }
  return errs;
}

export default function AddProjectModal({ onClose, onCreated }) {
  const { setToast } = useAppContext();
  const [step, setStep]     = useState(0);
  const [form, setForm]     = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const update = (patch) => setForm(f => ({ ...f, ...patch }));

  const next = () => {
    const errs = validateStep(step, form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setErrors({});
    setStep(s => Math.min(STEPS.length - 1, s + 1));
  };

  const back = () => { setErrors({}); setStep(s => Math.max(0, s - 1)); };

  const submit = async () => {
    const errs = validateStep(step, form);
    if (Object.keys(errs).length) { setErrors(errs); return; }

    setLoading(true);
    try {
      const payload = {
        ...form,
        startingPrice: form.startingPrice ? parseInt(form.startingPrice, 10) : undefined,
        latitude:  form.latitude  ? parseFloat(form.latitude)  : undefined,
        longitude: form.longitude ? parseFloat(form.longitude) : undefined,
        paymentPlans: form.paymentPlans
          .filter(p => p.downPaymentPercent !== '' && p.installmentYears !== '')
          .map(p => ({
            downPaymentPercent: parseInt(p.downPaymentPercent, 10),
            installmentYears:   parseInt(p.installmentYears, 10),
            notes: p.notes || null,
          })),
      };

      // Remove empty optional strings
      Object.keys(payload).forEach(k => {
        if (payload[k] === '') payload[k] = undefined;
      });

      const created = await apiFetch('/api/dev-projects', {
        method: 'POST',
        body: JSON.stringify(payload),
      });

      setToast({ msg: `Project "${created.name}" created successfully.`, type: 'success' });
      onCreated?.(created);
      onClose();
    } catch (err) {
      setToast({ msg: err.message || 'Failed to create project.', type: 'error' });
    } finally {
      setLoading(false);
    }
  };

  const stepComponents = [
    <Step1BasicInfo  key={0} form={form} update={update} errors={errors} />,
    <Step2Location   key={1} form={form} update={update} errors={errors} />,
    <Step3Financials key={2} form={form} update={update} errors={errors} />,
    <Step4Specs      key={3} form={form} update={update} errors={errors} />,
    <Step5Media      key={4} form={form} update={update} errors={errors} />,
    <Step6Seo        key={5} form={form} update={update} errors={errors} />,
  ];

  const isLast = step === STEPS.length - 1;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,.75)',
        zIndex: 9500, display: 'flex', alignItems: 'center', justifyContent: 'center',
        backdropFilter: 'blur(4px)',
      }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <div style={{
        background: '#0a1628',
        border: '1px solid rgba(201,168,76,.2)',
        borderRadius: 12,
        width: 'min(860px, 96vw)',
        maxHeight: '92vh',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        boxShadow: '0 24px 64px rgba(0,0,0,.6)',
      }}>

        {/* Header */}
        <div style={{
          padding: '20px 26px 0',
          borderBottom: '1px solid rgba(201,168,76,.1)',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
            <div>
              <div style={{ fontFamily: 'var(--serif)', color: '#fff', fontSize: '1.4rem', fontWeight: 300 }}>
                New Development Project
              </div>
              <div style={{ color: 'rgba(255,255,255,.28)', fontSize: '.7rem', marginTop: 2 }}>
                Step {step + 1} of {STEPS.length} — {STEPS[step]}
              </div>
            </div>
            <button onClick={onClose} style={{
              background: 'none', border: 'none', color: 'rgba(255,255,255,.3)',
              cursor: 'pointer', padding: 4, marginTop: -2,
            }}>
              <X size={18} />
            </button>
          </div>
          <StepProgressBar steps={STEPS} current={step} />
        </div>

        {/* Body */}
        <div style={{ padding: '22px 26px', overflowY: 'auto', flex: 1, maxHeight: '62vh' }}>
          {stepComponents[step]}
        </div>

        {/* Footer */}
        <div style={{
          padding: '14px 26px',
          borderTop: '1px solid rgba(201,168,76,.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexShrink: 0,
          background: 'rgba(0,0,0,.15)',
        }}>
          <button
            onClick={back}
            disabled={step === 0}
            style={{
              display: 'flex', alignItems: 'center', gap: 5,
              background: 'none', border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 5, padding: '9px 18px',
              color: step === 0 ? 'rgba(255,255,255,.2)' : 'rgba(255,255,255,.6)',
              cursor: step === 0 ? 'default' : 'pointer',
              fontSize: '.82rem', fontFamily: 'var(--sans)',
              transition: 'all .18s',
            }}
          >
            <ChevronLeft size={14} /> Back
          </button>

          <div style={{ display: 'flex', gap: 8 }}>
            <button onClick={onClose} style={{
              background: 'none', border: '1px solid rgba(255,255,255,.12)',
              borderRadius: 5, padding: '9px 18px',
              color: 'rgba(255,255,255,.4)', cursor: 'pointer',
              fontSize: '.82rem', fontFamily: 'var(--sans)',
            }}>
              Cancel
            </button>

            {isLast ? (
              <button
                onClick={submit}
                disabled={loading}
                className="btn-g"
                style={{ borderRadius: 5, padding: '9px 26px', fontSize: '.84rem', opacity: loading ? .7 : 1 }}
              >
                {loading ? 'Creating…' : 'Create Project'}
              </button>
            ) : (
              <button
                onClick={next}
                className="btn-g"
                style={{ display: 'flex', alignItems: 'center', gap: 5, borderRadius: 5, padding: '9px 22px', fontSize: '.84rem' }}
              >
                Next <ChevronRight size={14} />
              </button>
            )}
          </div>
        </div>

      </div>
    </div>
  );
}
